import { capitalize } from '@vy/string';
import type { SystemEvent } from '@vy/types';
import { SYSTEM_EVENT_TYPES, SYSTEM_TYPES } from '@vy/types';
import {
  isNullOrUndefined,
  isNotNullOrUndefined,
  raiseUnreachable,
  validateNotNullOrUndefined,
  isNotEmptyArray,
  every,
  isString,
} from '@vy/utils';

export function getSystemEventLabel(systemEvent: SystemEvent): string {
  switch (systemEvent.type) {
    case SYSTEM_EVENT_TYPES.INVOICE_CREATED:
      return 'Created';
    case SYSTEM_EVENT_TYPES.INVOICE_UPDATED:
      return getModifiedInvoiceLabel(systemEvent);
    case SYSTEM_EVENT_TYPES.INVOICE_APPROVED:
      return 'Approved';
    case SYSTEM_EVENT_TYPES.INVOICE_PAID:
      return 'Paid';
    case SYSTEM_EVENT_TYPES.INVOICE_SYNCED:
      return getSyncedLabel(systemEvent);
    case SYSTEM_EVENT_TYPES.INVOICE_SYNC_FAILED:
      return getFailedSyncLabel(systemEvent);
    case SYSTEM_EVENT_TYPES.INVOICE_EMAIL_OPENED:
      return 'Email opened';
    default:
      return 'Event';
  }
}

function getSyncedLabel(systemEvent: SystemEvent) {
  validateNotNullOrUndefined(systemEvent.system);

  switch (systemEvent.system) {
    case SYSTEM_TYPES.NetSuite:
      return 'Synced via NetSuite';
    case SYSTEM_TYPES.Stripe:
      return 'Synced via Stripe';
    default:
      return raiseUnreachable(systemEvent.system);
  }
}

function getFailedSyncLabel(systemEvent: SystemEvent) {
  validateNotNullOrUndefined(systemEvent.system);

  switch (systemEvent.system) {
    case SYSTEM_TYPES.NetSuite:
      return 'Failed to sync to NetSuite';
    case SYSTEM_TYPES.Stripe:
      return 'Failed to sync to Stripe';
    default:
      return raiseUnreachable(systemEvent.system);
  }
}

function getModifiedInvoiceLabel(systemEvent: SystemEvent) {
  const editedByArray = systemEvent.metadata?.editedBy;
  const changedFieldsArray = systemEvent.metadata?.changedFields;
  const currencyArray = systemEvent.metadata?.currency;
  const exchangeRateArray = systemEvent.metadata?.modifiedExchangeRate;

  const editedBy = isNotEmptyArray(editedByArray) ? editedByArray[0] : undefined;
  const changedFields: string[] =
    every(changedFieldsArray, (field) => isString(field)) && isNotEmptyArray(changedFieldsArray)
      ? (changedFieldsArray as string[])
      : [];

  const isCurrencyChange = changedFields.includes('currency') || changedFields.includes('exchange rate');

  if (isCurrencyChange && isNotEmptyArray(currencyArray)) {
    const newCurrency = currencyArray[0];
    const exchangeRate = isNotEmptyArray(exchangeRateArray) ? exchangeRateArray[0] : undefined;
    const exchangeRateText = isNotNullOrUndefined(exchangeRate) ? ` (exchange rate: ${exchangeRate})` : '';

    return `Currency changed to ${newCurrency}${exchangeRateText}`;
  }

  if (isNullOrUndefined(editedBy)) {
    return 'Invoice edited';
  }

  if (changedFields?.length === 1) {
    const changedFieldsText = changedFields.join(', ');
    return `${capitalize(changedFieldsText)} edited by ${editedBy}`;
  }

  return `Invoice edited by ${editedBy}`;
}
